/**
 * Reports what played to the engine's play log.
 *
 * It listens to the player rather than living inside it: while a track is the
 * current one and the player is playing, the seconds are added up; when the
 * player moves on (next song, skip, a new queue, stop) the track is reported
 * with the seconds actually heard and whether it reached its end. The engine
 * decides what counts as a play (30 s, half of a short track, or to the end)
 * and does not store skips under 5 s.
 *
 * A queue item may carry a `reason` (Radio's rules set `rule:<id>`), so a
 * clip's play can be counted against the rule that put it there.
 */

import { engineBase } from "../api";
import { withToken } from "./session";
import { bus } from "./bus";
import { player, type Player, type PlayerSnapshot } from "./player";

interface Current {
  key: string;
  trackId: string;
  startedAt: string;
  heard: number;
  lastAt: number | null;
  position: number;
  end: number;
  reason?: string;
  source: string;
}

/** Local time as the log stores it: "2026-10-20T07:15:00". */
export function localStamp(d = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function playReport(c: Current) {
  return {
    trackId: c.trackId,
    startedAt: c.startedAt,
    seconds: Math.round(c.heard * 10) / 10,
    // Within two seconds of where it hands over (its mix out, or its end).
    finished: c.position >= c.end - 2,
    source: c.source,
    reason: c.reason,
  };
}

/** Follow a player and call `send` once per track it moves on from. Returns a stop function. */
export function followPlays(
  source: { subscribe: (fn: () => void) => () => void; get: () => PlayerSnapshot },
  send: (report: ReturnType<typeof playReport>) => void,
  now: () => number = () => performance.now(),
) {
  let cur: Current | null = null;

  const update = () => {
    const s = source.get();
    const t = now();
    if (cur && cur.lastAt !== null) cur.heard += (t - cur.lastAt) / 1000;
    const item = s.current;
    if (cur && cur.key !== item?.key) {
      send(playReport(cur));
      cur = null;
    }
    if (item && !cur) {
      cur = {
        key: item.key,
        trackId: item.track.id,
        startedAt: localStamp(),
        heard: 0,
        lastAt: null,
        position: s.position,
        end: item.track.cueOut ?? item.track.duration,
        reason: item.reason,
        source: s.source?.kind ?? "player",
      };
    }
    if (cur) {
      cur.position = Math.max(cur.position, s.position);
      cur.lastAt = s.playing ? t : null;
    }
  };

  const off = source.subscribe(update);
  const flush = () => {
    update();
    if (cur) send(playReport(cur));
    cur = null;
  };
  return { stop: off, flush };
}

let started = false;

export interface PlayReport {
  trackId: string;
  startedAt: string;
  seconds: number;
  finished: boolean;
  source?: string | null;
  reason?: string | null;
}

function post(report: PlayReport, beacon = false) {
  const body = JSON.stringify(report);
  const url = `${engineBase()}/api/plays`;
  // Closing the window: a beacon still gets through (plain text avoids a preflight).
  // A beacon cannot send headers: the token goes in the address (core/session.ts).
  if (beacon && navigator.sendBeacon?.(withToken(url), new Blob([body], { type: "text/plain" }))) return;
  void fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body }).catch(() => undefined);
}

/** Log a play of something the player itself did not play (Radio's carts). */
export function reportPlay(r: { trackId: string; startedAt: Date; seconds: number; finished: boolean; source: string; reason?: string }) {
  post({ trackId: r.trackId, startedAt: localStamp(r.startedAt), seconds: Math.round(r.seconds * 10) / 10,
    finished: r.finished, source: r.source, reason: r.reason ?? null });
}

let closing = false;
const flushers = new Set<() => void>();
/** Plays of the library's player are not logged (Radio sets this: they are previews at the desk). */
let libraryQuiet = false;

/**
 * Report a player's plays to the engine's log: every track it moves on from,
 * and every clip it plays over a song. `when` can hold reports back (a
 * preview player). Returns the stop function.
 */
export function logPlaysOf(p: Player, when: () => boolean = () => true): () => void {
  const follower = followPlays(p, (r) => when() && post(r, closing));
  // A clip played over a song is a play of its own.
  const off = bus.on(`${p.events}.overlay`, (e) => {
    if (!when()) return;
    const d = e.data as { trackId: string; reason?: string; startedAt: number; seconds: number; finished: boolean };
    post({ trackId: d.trackId, startedAt: localStamp(new Date(d.startedAt)), seconds: Math.round(d.seconds * 10) / 10,
      finished: d.finished, source: "over-song", reason: d.reason });
  });
  flushers.add(follower.flush);
  return () => {
    follower.stop();
    off();
    flushers.delete(follower.flush);
  };
}

/**
 * Whether the library's own player is logged. A plugin that puts music on air
 * on its own player (Radio) turns this off: what plays in Songs is then a
 * preview at the desk, not a play.
 */
export function setLibraryLogged(on: boolean) {
  libraryQuiet = !on;
}

/** Start reporting the app's player to the engine. Safe to call more than once. */
export function startPlayLog() {
  if (started) return;
  started = true;
  logPlaysOf(player, () => !libraryQuiet);
  window.addEventListener("pagehide", () => {
    closing = true;
    flushers.forEach((flush) => flush());
  });
}
